import React from "react";
import { SurveyInterface } from "../../domain/interfaces/survey_interface";
import { useNavigate } from "react-router-dom";
import GroupIcon from '../../../../assets/images/group_icon.svg?react';
import CalendarClockIcon from '../../../../assets/images/calendar_clock_icon.svg?react';
import UvibesPointIcon from '../../../../assets/images/uvibes_point.svg?react';
import type { TFunction } from 'i18next';
import { useTranslation } from '../../../../i18n/useTranslation';

const getStatusText = (t: TFunction, startDate: Date, endDate: Date) => {
    const now = new Date();

    if (startDate > now) {
        const diffTime = startDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const diffMinutes = Math.ceil(diffTime / (1000 * 60));

        if (diffDays > 1) {
            return t('survey.status.start_days', { count: diffDays });
        } else if (diffMinutes > 60) {
            const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
            return t('survey.status.start_hours', { count: diffHours });
        } else {
            return t('survey.status.start_minutes', { count: diffMinutes });
        }
    }

    if (endDate < now) {
        return t('survey.status.ended');
    }

    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffMinutes = Math.ceil(diffTime / (1000 * 60));

    if (diffDays > 1) {
        return t('survey.status.end_days', { count: diffDays });
    } else if (diffMinutes > 60) {
        const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
        return t('survey.status.end_hours', { count: diffHours });
    } else {
        return t('survey.status.end_minutes', { count: diffMinutes });
    }
};

const SurveyCardElement: React.FC<{ survey: SurveyInterface, color: string }> = ({ survey, color }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const now = new Date();
    const isFull = survey.expectedRespondents > 0 && survey.nbAnswers >= survey.expectedRespondents;
    const canOpen = survey.startDate <= now && survey.endDate >= now && (!isFull || survey.answered);

    return (
        <div
            className={`p-3 flex flex-col w-full rounded-[20px] gap-3.75 overflow-clip relative ${canOpen ? 'cursor-pointer' : 'cursor-not-allowed'}`}
            style={{ backgroundColor: color, boxShadow: '0 0 4px rgba(0, 0, 0, 0.04), 0 8px 16px rgba(0, 0, 0, 0.08)' }}
            onClick={() => {
                if (survey.questions.length > 0 && canOpen) {
                    navigate(`/survey/${survey.id}/${survey.questions[0].id}`, { state: survey });
                }
            }}
        >
            <svg  viewBox="0 0 262 133" fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute top-1/2 -translate-y-1/2 right-1.25 h-[200%] aspect-square">
                <mask id="mask0_2126_5866" maskUnits="userSpaceOnUse" x="0" y="-74" width="262" height="262">
                    <rect y="-74" width="262" height="262" fill="#D9D9D9"/>
                </mask>
                <g mask="url(#mask0_2126_5866)">
                    <path
                        d="M109.167 168.334C103.163 168.334 98.0232 166.196 93.7475 161.92C89.4718 157.644 87.334 152.505 87.334 146.5C87.334 140.496 89.4718 135.356 93.7475 131.081C98.0232 126.805 103.163 124.667 109.167 124.667H218.334C224.338 124.667 229.478 126.805 233.754 131.081C238.029 135.356 240.167 140.496 240.167 146.5C240.167 152.505 238.029 157.644 233.754 161.92C229.478 166.196 224.338 168.334 218.334 168.334H109.167ZM109.167 102.834C103.163 102.834 98.0232 100.696 93.7475 96.4201C89.4718 92.1444 87.334 87.0045 87.334 81.0003C87.334 74.9962 89.4718 69.8562 93.7475 65.5805C98.0232 61.3048 103.163 59.167 109.167 59.167H218.334C224.338 59.167 229.478 61.3048 233.754 65.5805C238.029 69.8562 240.167 74.9962 240.167 81.0003C240.167 87.0045 238.029 92.1444 233.754 96.4201C229.478 100.696 224.338 102.834 218.334 102.834H109.167ZM109.167 37.3337C103.163 37.3337 98.0232 35.1958 93.7475 30.9201C89.4718 26.6444 87.334 21.5045 87.334 15.5003C87.334 9.49616 89.4718 4.35623 93.7475 0.0805345C98.0232 -4.19516 103.163 -6.33301 109.167 -6.33301H218.334C224.338 -6.33301 229.478 -4.19516 233.754 0.0805345C238.029 4.35623 240.167 9.49616 240.167 15.5003C240.167 21.5045 238.029 26.6444 233.754 30.9201C229.478 35.1958 224.338 37.3337 218.334 37.3337H109.167ZM43.6673 37.3337C37.6632 37.3337 32.5232 35.1958 28.2475 30.9201C23.9718 26.6444 21.834 21.5045 21.834 15.5003C21.834 9.49616 23.9718 4.35623 28.2475 0.0805345C32.5232 -4.19516 37.6632 -6.33301 43.6673 -6.33301C49.6715 -6.33301 54.8114 -4.19516 59.0871 0.0805345C63.3628 4.35623 65.5007 9.49616 65.5007 15.5003C65.5007 21.5045 63.3628 26.6444 59.0871 30.9201C54.8114 35.1958 49.6715 37.3337 43.6673 37.3337ZM43.6673 102.834C37.6632 102.834 32.5232 100.696 28.2475 96.4201C23.9718 92.1444 21.834 87.0045 21.834 81.0003C21.834 74.9962 23.9718 69.8562 28.2475 65.5805C32.5232 61.3048 37.6632 59.167 43.6673 59.167C49.6715 59.167 54.8114 61.3048 59.0871 65.5805C63.3628 69.8562 65.5007 74.9962 65.5007 81.0003C65.5007 87.0045 63.3628 92.1444 59.0871 96.4201C54.8114 100.696 49.6715 102.834 43.6673 102.834ZM43.6673 168.334C37.6632 168.334 32.5232 166.196 28.2475 161.92C23.9718 157.644 21.834 152.505 21.834 146.5C21.834 140.496 23.9718 135.356 28.2475 131.081C32.5232 126.805 37.6632 124.667 43.6673 124.667C49.6715 124.667 54.8114 126.805 59.0871 131.081C63.3628 135.356 65.5007 140.496 65.5007 146.5C65.5007 152.505 63.3628 157.644 59.0871 161.92C54.8114 166.196 49.6715 168.334 43.6673 168.334Z"
                        fill="white"
                        fillOpacity="0.1"
                    />
                </g>
            </svg>

            <p className="text-white font-bold text-sm leading-snug min-h-10">{survey.title}</p>

            <div className="flex items-end justify-between gap-3">
                <div className="flex flex-col justify-end gap-1.5 text-white min-h-13.5">
                    {survey.isResultPublic === true && <div className="flex items-center gap-2.5">
                        <GroupIcon className="h-6 w-6"/>
                        <span className="text-sm">{survey.nbAnswers === 0 ? t('survey.card.no_answer') : t('survey.card.answers', { count: survey.nbAnswers })}</span>
                    </div>}
                    <div className="flex items-center gap-2.5">
                        <CalendarClockIcon className="h-6 w-6"/>
                        <span className="text-sm">{getStatusText(t, survey.startDate, survey.endDate)}</span>
                    </div>
                </div>

                <div className="flex items-center justify-center bg-white rounded-full py-1.5 gap-1.25 px-2 min-w-35">
                    <span className="font-semibold text-xs">
                        {survey.answered
                            ? t('survey.card.already_answered')
                            : isFull
                                ? t('survey.card.quota_reached')
                                : t('survey.card.points_to_win', { count: survey.nbPointsLeft })}
                    </span>
                    {!survey.answered && !isFull && <UvibesPointIcon className="h-4.5 w-4.5" />}
                </div>
            </div>
        </div>
    );

}

export default SurveyCardElement;
